import { Form, Formik } from "formik";
import React from "react";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";

import { AdministrativeGender } from "/apollo/schema";
import Button from "/component/base/Button";
import Modal from "/component/base/Modal";
import Radio from "/component/base/Radio";
import Text from "/component/base/Text";
import FormContent from "/component/partial/FormContent";
import FormattedTextField from "/component/partial/formik/FormattedTextField";
import RadioGroupField from "/component/partial/formik/RadioGroupField";
import TextField from "/component/partial/formik/TextField";
import { useToastContext } from "/component/provider/ToastProvider";
import { useAmwell, useTranslation } from "/hook";
import { useAnalytics } from "/hook/useAnalytics/useAnalytics";
import { layout } from "/styles";
import { maxMinorAgeTest } from "/util/date";

import { DependentAddFormValues, Props } from "./TelehealthDependentAddModal.types";

const TelehealthDependentAddModal = ({ isOpen, close }: Props) => {
  const { t } = useTranslation("modal-telehealthDependentAdd");
  const { dependents, enrollDependent, sdk, updateDependents } = useAmwell();
  const { showToast } = useToastContext();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { recordAnalyticsEvent } = useAnalytics();

  const callbackValidation = Yup.object().shape({
    firstName: Yup.string().required(t("error.required")),
    lastName: Yup.string().required(t("error.required")),
    dob: Yup.string()
      .required(t("error.required"))
      .matches(/^(0[1-9]|1[012])[/](0[1-9]|[12][0-9]|3[01])[/](19|20)\d\d$/, {
        message: t("error.dobInvalid"),
      })
      .test("maxAgeTest", t("error.maxAge"), (value) => {
        return maxMinorAgeTest(value);
      }),
    // .required(t("error.required")),
    legalSex: Yup.string().required(t("error.required")),
  });

  const onSubmit = async (values: DependentAddFormValues) => {
    try {
      if (!sdk || !sdk?.initialized) {
        throw Error("Amwell SDK is not defined");
      }
      setIsSubmitting(true);
      const registrationRequest = sdk?.consumerService.newDependentRegistration();
      registrationRequest.sourceId = uuidv4();
      registrationRequest.firstName = values.firstName || "";
      registrationRequest.lastName = values.lastName || "";
      registrationRequest.dob = new Date(values.dob);
      registrationRequest.gender =
        values.legalSex === AdministrativeGender.MALE
          ? "M"
          : values.legalSex === AdministrativeGender.FEMALE
          ? "F"
          : "U";
      registrationRequest.performDependentCheck = false;

      const childEnrolled = await enrollDependent(registrationRequest);
      if (!childEnrolled) {
        if (registrationRequest.dob > new Date()) {
          showToast({
            message: t("toast.errorFuture"),
            icon: "alert",
          });
        } else {
          showToast({
            message: t("toast.error"),
            icon: "alert",
          });
        }
      } else {
        recordAnalyticsEvent("amwellAddDependentComplete");
        const _dependents = [...dependents, childEnrolled];
        updateDependents(_dependents);
        showToast({
          message: t("toast.success"),
          icon: "checkmark",
        });
        close();
      }
    } catch (err) {
      showToast({
        message: t("toast.error"),
        icon: "alert",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      validateOnMount
      enableReinitialize
      initialValues={{ firstName: "", lastName: "", dob: "", legalSex: "" }}
      validationSchema={callbackValidation}
      onSubmit={onSubmit}
    >
      {({ submitForm, isValid }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            key="TelehealthDependentAddModal"
            close={close}
            header={<Modal.Header title="" close={close}></Modal.Header>}
            footer={
              <Modal.FooterWithLargeButton>
                <Button
                  onClick={submitForm}
                  isDisabled={!isValid}
                  isLoading={isSubmitting}
                  type="submit"
                  variant="primary"
                  fullWidth="percent"
                >
                  {t("save")}
                </Button>
              </Modal.FooterWithLargeButton>
            }
          >
            <FormContent>
              <Modal.Title>
                <Text color="textDefault" variant="title1">
                  {t("title")}
                </Text>
              </Modal.Title>

              <TextField label={t("label.firstName")} name={"firstName"} type="text" />
              <TextField label={t("label.lastName")} name={"lastName"} type="text" />
              <FormattedTextField
                label={t("label.dateOfBirth")}
                name={"dob"}
                placeholder="MM/DD/YYYY"
                type="text"
                mask="99/99/9999"
              />
              <RadioGroupField
                name="legalSex"
                label={t("label.legalSex")}
                css={[{ marginTop: 16 }]}
              >
                <div css={[layout.flexLTR, layout.spacedChildrenHorizontal()]}>
                  <RadioGroupField.Option value={AdministrativeGender.MALE}>
                    {({ checked }) => (
                      <Radio
                        label={
                          <RadioGroupField.Label variant="body1">
                            {t("legalSex.male")}
                          </RadioGroupField.Label>
                        }
                        checked={checked}
                        onChange={() => null}
                        labelBefore={false}
                      />
                    )}
                  </RadioGroupField.Option>
                  <RadioGroupField.Option value={AdministrativeGender.FEMALE}>
                    {({ checked }) => (
                      <Radio
                        label={
                          <RadioGroupField.Label variant="body1">
                            {t("legalSex.female")}
                          </RadioGroupField.Label>
                        }
                        checked={checked}
                        onChange={() => null}
                        labelBefore={false}
                      />
                    )}
                  </RadioGroupField.Option>
                </div>
              </RadioGroupField>
            </FormContent>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default TelehealthDependentAddModal;
